import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

const LIMITS = [
  { kib: 512, label: "0.5 MB/s" },
  { kib: 1024, label: "1 MB/s" },
  { kib: 2048, label: "2 MB/s" },
  { kib: 5120, label: "5 MB/s" },
];

const fmtSpeed = (n) => {
  if (!n) return "0 KB/s";
  return n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB/s` : `${Math.round(n / 1024)} KB/s`;
};

// One switch for every download in qBittorrent: "Slow down" turns on its alternative speed limit
// (a global cap on all current and new downloads), "Speed up" turns it off again. Handy during
// working hours so the downloads do not eat the office bandwidth.
export default function ThrottleBar() {
  const [state, setState] = useState(null);
  const [limit, setLimit] = useState(1024);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // qBittorrent can answer slowly while the box is busy writing downloads to the NAS, so polls never
  // overlap, a failed poll keeps the last known state, and the buttons stay usable.
  const inFlight = useRef(false);
  const seq = useRef(0); // bumped by every button press so an older, slower poll cannot overwrite the new state
  const poll = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    const mine = seq.current;
    try {
      const { data } = await api.get("/qbit/throttle", { timeout: 45000 });
      if (mine !== seq.current) return;
      setState(data); setErr("");
      if (data.slow && data.limit_kib) setLimit(data.limit_kib);
    } catch (e) {
      setErr(e.response?.data?.detail ? "qBittorrent is slow to answer, retrying…" : "Could not reach the server, retrying…");
    } finally { inFlight.current = false; }
  };
  useEffect(() => {
    poll();
    const t = setInterval(poll, 10000);
    return () => clearInterval(t);
  }, []);

  const apply = async (slow) => {
    setBusy(true);
    seq.current += 1;
    try {
      const { data } = await api.post("/qbit/throttle", { slow, limit_kib: limit }, { timeout: 60000 });
      seq.current += 1;
      setState(data); setErr("");
      toast.success(slow ? `Downloads slowed to ${LIMITS.find((l) => l.kib === limit)?.label || `${limit} KB/s`}` : "Downloads back at full speed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not change the speed");
    } finally { setBusy(false); }
  };

  const slow = !!state?.slow;
  return (
    <div className="mt-8 flex items-center gap-4 flex-wrap border border-[#222] bg-[#0F0F0F] px-5 py-4 max-w-3xl" data-testid="throttle-bar">
      <div className="min-w-0">
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">Download speed</span>
        <p className={`text-sm mt-1 ${slow ? "text-[#fcd34d]" : "text-[#86efac]"}`} data-testid="throttle-state">
          {state === null ? (err ? <span className="text-[#fcd34d]">{err}</span> : "Reading qBittorrent…")
            : slow ? `Slowed (capped at ${fmtSpeed((state.limit_kib || 0) * 1024)})` : "Full speed"}
          {err && state !== null && <span className="text-[#fcd34d]"> · {err}</span>}
          {state && (
            <span className="text-[#8A8A8A]"> · now ↓ {fmtSpeed(state.dl_speed)} · {state.downloading} downloading</span>
          )}
        </p>
      </div>
      <div className="flex items-center gap-2 ml-auto">
        {!slow && (
          <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}
            className="bg-[#0A0A0A] border border-[#222] text-[#8A8A8A] text-xs px-2 py-2" data-testid="throttle-limit">
            {LIMITS.map((l) => <option key={l.kib} value={l.kib}>{l.label}</option>)}
          </select>
        )}
        {slow ? (
          <button onClick={() => apply(false)} disabled={busy}
            className="text-xs uppercase tracking-[0.2em] bg-[#D9381E] hover:bg-[#ED4B32] text-white px-4 py-2 disabled:opacity-50"
            data-testid="throttle-speedup">{busy ? "Working…" : "Speed up"}</button>
        ) : (
          <button onClick={() => apply(true)} disabled={busy}
            className="text-xs uppercase tracking-[0.2em] border border-[#fcd34d] text-[#fcd34d] hover:bg-[#fcd34d] hover:text-black px-4 py-2 disabled:opacity-50"
            data-testid="throttle-slowdown">{busy ? "Working…" : "Slow down"}</button>
        )}
      </div>
    </div>
  );
}
